import type { HugeiconsProps } from '@hugeicons/react-native';
import { Menu01Icon } from '@hugeicons-pro/core-solid-rounded';
import React from 'react';

import { Button, ButtonText } from '@/components/gluestack/button';
import { Heading } from '~/components/gluestack/heading';
import { HStack } from '~/components/gluestack/hstack';
import { Text } from '~/components/gluestack/text';
import { HugeIconsWrapper } from '~/components/ui/HugeIconsWrapper';

export interface IHeaderMenuSelectorItem<T extends string> {
  value: T;
  label: string;
  icon?: HugeiconsProps['icon'];
  solidIcon?: HugeiconsProps['icon'];
  separatorBefore?: boolean;
}

export type HeaderMenuSelectorTriggerVariant = 'header' | 'button';

export interface IHeaderMenuSelectorProps<T extends string> {
  value: T;
  onChange: (value: T) => void;
  items: IHeaderMenuSelectorItem<T>[];
  triggerLabel: string;
  /**
   * Visual style of the trigger. `header` (default) renders the inline
   * heading-style trigger used in screen headers; `button` renders a
   * solid primary button used where the selector needs more visual weight.
   */
  triggerVariant?: HeaderMenuSelectorTriggerVariant;
}

export function getHeaderMenuSelectorSelectedIcon<T extends string>(
  value: T,
  items: IHeaderMenuSelectorItem<T>[]
) {
  const selectedItem = items.find((item) => item.value === value);
  return selectedItem?.solidIcon ?? selectedItem?.icon ?? null;
}

export function getHeaderMenuSelectorButtonIcon<T extends string>(
  value: T,
  items: IHeaderMenuSelectorItem<T>[]
) {
  return getHeaderMenuSelectorSelectedIcon(value, items) ?? Menu01Icon;
}

interface IHeaderMenuSelectorTitleProps<T extends string> {
  value: T;
  items: IHeaderMenuSelectorItem<T>[];
  title: string;
  icon?: HugeiconsProps['icon'];
}

export function HeaderMenuSelectorTitle<T extends string>({
  value,
  items,
  title,
  icon,
}: IHeaderMenuSelectorTitleProps<T>) {
  const titleIcon = icon ?? getHeaderMenuSelectorSelectedIcon(value, items);

  return (
    <HStack className="items-center gap-2">
      {titleIcon && (
        <HugeIconsWrapper icon={titleIcon} size={28} className="color-tertiary-400" />
      )}
      <Text size="2xl" bold className="text-tertiary-500">{title}</Text>
    </HStack>
  );
}

interface IHeaderMenuSelectorButtonTriggerProps {
  triggerLabel: string;
  icon: HugeiconsProps['icon'];
  triggerProps?: React.ComponentProps<typeof Button>;
}

export function HeaderMenuSelectorButtonTrigger({
  triggerLabel,
  icon,
  triggerProps,
}: IHeaderMenuSelectorButtonTriggerProps) {
  return (
    <Button variant="solid" action="primary" size="sm" {...triggerProps}>
      <HugeIconsWrapper icon={icon} size={18} className="text-white" />
      <ButtonText className="text-white">{triggerLabel}</ButtonText>
    </Button>
  );
}

export function HeaderMenuSelectorHeaderTrigger({
  triggerLabel,
}: {
  triggerLabel: string;
}) {
  return (
    <HStack space="md" className="mr-1 items-center gap-2">
      <HugeIconsWrapper icon={Menu01Icon} size={22} className="color-tertiary-400" />
      <Heading size="md" className="text-tertiary-500 font-extrabold">
        {triggerLabel}
      </Heading>
    </HStack>
  );
}
